/* ══════════════════════════════════════════════════════════════
   NUMA SLEEP — front-end
   Plain JS, no framework, no build. Safe to paste into a Shopify theme.
   ══════════════════════════════════════════════════════════════ */
(() => {
'use strict';

const S = window.STORE;
const MODE = window.CART_MODE || 'local';
// Shopify build: events go to window.TRACK_ENDPOINT (the Vercel collector).
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const money = (n) => S.symbol + Number(n).toLocaleString('en-US');

/* ───────────────────────────────────────────── analytics ───────── */

const SID_KEY = 'cc_sid';
let sid = localStorage.getItem(SID_KEY);
if (!sid) {
  sid = Math.random().toString(36).slice(2) + Date.now().toString(36);
  localStorage.setItem(SID_KEY, sid);
}

const source = (() => {
  const p = new URLSearchParams(location.search);
  const utm = ['utm_source', 'utm_medium', 'utm_campaign'].map((k) => p.get(k)).filter(Boolean).join('/');
  if (utm) return utm;
  if (document.referrer && !document.referrer.includes(location.host)) {
    try { return new URL(document.referrer).hostname; } catch { /* ignore */ }
  }
  return 'direct';
})();

const device = matchMedia('(max-width:900px)').matches ? 'mobile' : 'desktop';
let queue = [];
let flushTimer = null;

const TRACK_URL = () => window.TRACK_ENDPOINT || '/api/track';
const LEAD_URL = () => window.LEAD_ENDPOINT || '/api/lead';

function flush(useBeacon) {
  if (!queue.length) return;
  const body = JSON.stringify(queue);
  queue = [];
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(TRACK_URL(), new Blob([body], { type: 'application/json' }));
  } else {
    fetch(TRACK_URL(), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }
}

/* TikTok's standard event names, so the ad platform can optimise towards them.
   Anything not on this list stays in our own funnel only. */
const TIKTOK_EVENTS = {
  add_to_cart: 'AddToCart',
  checkout_start: 'InitiateCheckout',
  lead: 'SubmitForm',
  play_crunch: 'ViewContent',
};

function track(type, props = {}) {
  queue.push({ session: sid, type, props, source, device, path: location.pathname });
  clearTimeout(flushTimer);
  flushTimer = setTimeout(() => flush(false), 900);

  const tt = TIKTOK_EVENTS[type];
  if (tt && window.ttq) {
    window.ttq.track(tt, {
      content_id: props.sku || S.product.handle,
      content_name: S.product.title,
      content_type: 'product',
      quantity: props.qty || 1,
      value: props.value || props.price || 0,
      currency: S.currency,
    });
  }
  if (window.ym) window.ym(window.YM_ID, 'reachGoal', type, props);
  if (window.gtag) window.gtag('event', type, props);
}

/* Loaded only when a pixel ID is configured — no ID, no third-party script. */
function loadTikTokPixel(id) {
  if (!id || window.ttq) return;
  (function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    const ttq = (w[t] = w[t] || []);
    ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
    ttq.setAndDefer = function (obj, m) { obj[m] = function () { obj.push([m].concat([].slice.call(arguments, 0))); }; };
    for (const m of ttq.methods) ttq.setAndDefer(ttq, m);
    ttq.load = function (pixelId) {
      const url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {}; ttq._i[pixelId] = []; ttq._t = ttq._t || {}; ttq._t[pixelId] = +new Date();
      ttq._o = ttq._o || {}; ttq._o[pixelId] = {};
      const script = d.createElement('script');
      script.async = true; script.src = url + '?sdkid=' + pixelId + '&lib=' + t;
      d.head.appendChild(script);
    };
    ttq.load(id);
    ttq.page();
  })(window, document, 'ttq');
}

addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(true); });
addEventListener('pagehide', () => flush(true));

/* ───────────────────────────────────────────── crunch synth ───────── */
/* No audio file needed — the crunch is generated as granular noise. */

let actx = null;
let analyser = null;

function audioCtx() {
  if (!actx) {
    actx = new (window.AudioContext || window.webkitAudioContext)();
    analyser = actx.createAnalyser();
    analyser.fftSize = 64;
    analyser.smoothingTimeConstant = 0.55;
    analyser.connect(actx.destination);
  }
  if (actx.state === 'suspended') actx.resume();
  return actx;
}

function crunch(intensity = 1) {
  const ctx = audioCtx();
  const dur = 0.26 + Math.random() * 0.14;
  const len = Math.floor(dur * ctx.sampleRate);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);

  // Clusters of tiny impulses = the crinkle of stiff fibre.
  let i = 0;
  while (i < len) {
    const grain = Math.floor((0.0006 + Math.random() * 0.0038) * ctx.sampleRate);
    const amp = Math.pow(Math.random(), 1.8);
    for (let j = 0; j < grain && i + j < len; j++) {
      d[i + j] += (Math.random() * 2 - 1) * amp * Math.exp(-j / (grain * 0.34));
    }
    i += Math.floor((0.0008 + Math.random() * 0.0065) * ctx.sampleRate);
  }
  // Attack + decay over the whole burst.
  for (let k = 0; k < len; k++) {
    d[k] *= Math.min(1, k / (ctx.sampleRate * 0.008)) * Math.exp(-k / (len * 0.45));
  }

  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.playbackRate.value = 0.88 + Math.random() * 0.3;

  const band = ctx.createBiquadFilter();
  band.type = 'bandpass';
  band.frequency.value = 2300 + Math.random() * 1400;
  band.Q.value = 0.75;

  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 780;

  const gain = ctx.createGain();
  gain.gain.value = 0.34 * intensity;

  src.connect(band).connect(hp).connect(gain).connect(analyser);
  src.start();
}

/* visualiser bars */
const viz = $('#viz');
if (viz) {
  for (let i = 0; i < 26; i++) viz.appendChild(document.createElement('i'));
}
const bars = viz ? $$('i', viz) : [];
let vizRunning = false;

function runViz() {
  if (!analyser || vizRunning) return;
  vizRunning = true;
  const data = new Uint8Array(analyser.frequencyBinCount);
  let idle = 0;
  (function frame() {
    analyser.getByteFrequencyData(data);
    let sum = 0;
    bars.forEach((b, i) => {
      const v = data[Math.floor((i / bars.length) * data.length)] || 0;
      sum += v;
      b.style.height = 4 + (v / 255) * 40 + 'px';
      b.style.background = v > 90 ? 'var(--rose)' : 'var(--blush)';
    });
    idle = sum < 30 ? idle + 1 : 0;
    if (idle > 90) { vizRunning = false; bars.forEach((b) => (b.style.height = '4px')); return; }
    requestAnimationFrame(frame);
  })();
}

/* press-and-hold pad */
const pad = $('#pad');
const crunchCountEl = $('#crunchCount');
let crunchCount = 0;
let holdTimer = null;

let playTracked = false;

/* `origin` is only set when a human asked for the sound — the little confirmation
   crunch on add-to-cart must not pollute the play_crunch funnel step. */
function doCrunch(intensity = 1, origin = null) {
  crunch(intensity);
  runViz();
  crunchCount++;
  if (crunchCountEl) crunchCountEl.textContent = crunchCount;
  if (pad) {
    pad.classList.remove('ping');
    void pad.offsetWidth;
    pad.classList.add('ping');
  }
  if (origin && !playTracked) { playTracked = true; track('play_crunch', { source: origin }); }
  if (origin && crunchCount === 10) track('crunch_addict', { count: 10 });
}

function startHold() {
  if (!pad) return;
  pad.classList.add('down');
  doCrunch(1, 'pad');
  const tick = () => {
    doCrunch(0.75 + Math.random() * 0.35);
    holdTimer = setTimeout(tick, 90 + Math.random() * 150);
  };
  holdTimer = setTimeout(tick, 190);
}
function endHold() {
  clearTimeout(holdTimer);
  if (pad) pad.classList.remove('down');
}

if (pad) {
  pad.addEventListener('pointerdown', (e) => { e.preventDefault(); startHold(); });
  ['pointerup', 'pointerleave', 'pointercancel'].forEach((ev) => pad.addEventListener(ev, endHold));
  pad.addEventListener('keydown', (e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); doCrunch(1, 'pad'); } });
}

const heroCrunch = $('#heroCrunch');
if (heroCrunch) {
  heroCrunch.addEventListener('click', () => {
    $('#sound').scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => { doCrunch(1, 'hero'); setTimeout(() => doCrunch(0.9), 260); setTimeout(() => doCrunch(0.8), 520); }, 480);
  });
}

/* ───────────────────────────────────────────── product state ───────── */

const state = {
  size: S.product.sizes[0].id,
  color: S.product.colors.find((c) => c.status === 'in_stock').id,
  qty: 1,
};

const sizeOf = (id) => S.product.sizes.find((s) => s.id === id);
const colorOf = (id) => S.product.colors.find((c) => c.id === id);
const sku = () => `${state.size}-${state.color}`;
const unitPrice = () => sizeOf(state.size).price;

function renderStoreText() {
  const announcement = (device === 'mobile' && S.announcementShort) || S.announcement;
  $$('[data-store="announcement"]').forEach((el) => (el.textContent = announcement));
  $$('[data-store="title"]').forEach((el) => (el.textContent = S.product.title));
  $$('[data-store="price"]').forEach((el) => (el.textContent = money(S.product.sizes[0].price)));
  $('#year').textContent = new Date().getFullYear();
}

function renderOptions() {
  const sw = $('#swatches');
  sw.innerHTML = '';
  S.product.colors.forEach((c) => {
    const b = document.createElement('button');
    b.className = 'sw' + (c.id === state.color ? ' on' : '') + (c.status !== 'in_stock' ? ' soldout' : '');
    b.style.background = c.hex;
    b.title = c.status === 'in_stock' ? c.label : `${c.label} — next batch`;
    b.setAttribute('aria-label', b.title);
    b.addEventListener('click', () => {
      if (c.status !== 'in_stock') {
        track('waitlist_click', { color: c.id });
        toast(`${c.label} lands in batch 002 — checkout and we'll hold one.`);
        return;
      }
      state.color = c.id;
      track('variant_select', { type: 'color', color: c.id });
      renderOptions();
      renderPrice();
    });
    sw.appendChild(b);
  });

  const sz = $('#sizes');
  sz.innerHTML = '';
  S.product.sizes.forEach((s) => {
    const b = document.createElement('button');
    b.className = 'size' + (s.id === state.size ? ' on' : '');
    b.innerHTML = `<span class="size-l"><b>${s.label} · ${s.dims}</b><span>${s.note} · ${s.weight}</span></span><span class="size-p">${money(s.price)}</span>`;
    b.addEventListener('click', () => {
      state.size = s.id;
      track('variant_select', { type: 'size', size: s.id, price: s.price });
      renderOptions();
      renderPrice();
    });
    sz.appendChild(b);
  });

  $('#colorValue').textContent = colorOf(state.color).label;
  $('#sizeValue').textContent = `${sizeOf(state.size).label} · ${sizeOf(state.size).dims}`;
  $('#sbVariant').textContent = `${sizeOf(state.size).label} · ${colorOf(state.color).label}`;
}

function renderPrice() {
  const p = unitPrice();
  const ratio = sizeOf(state.size).price / S.product.sizes[0].price;
  const cmp = Math.round(S.product.compareAt * ratio);
  $('#price').textContent = money(p);
  $('#compare').textContent = money(cmp);
  $('#save').textContent = `Save ${Math.round((1 - p / cmp) * 100)}%`;
  $('#addPrice').textContent = money(p * state.qty);
  $('#sbPrice').textContent = money(p * state.qty);
  $('#qtyValue').textContent = state.qty;
}

$('#qtyMinus').addEventListener('click', () => { state.qty = Math.max(1, state.qty - 1); renderPrice(); });
$('#qtyPlus').addEventListener('click', () => { state.qty = Math.min(9, state.qty + 1); renderPrice(); });

/* ───────────────────────────────────────────── cart ───────── */
/* Adapter: swap CART_MODE to 'shopify' and this talks to the real store. */

const CART_KEY = 'cc_cart';
let cart = [];
try { cart = JSON.parse(localStorage.getItem(CART_KEY)) || []; } catch { cart = []; }

const cartApi = {
  async add(item) {
    if (MODE === 'shopify') {
      const id = S.product.shopifyVariantId[item.sku];
      if (!id) { console.warn('No Shopify variant id for', item.sku); return; }
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: item.qty }] }),
      });
      await cartApi.sync();
      return;
    }
    const found = cart.find((l) => l.sku === item.sku);
    if (found) found.qty = Math.min(9, found.qty + item.qty);
    else cart.push(item);
    cartApi.save();
  },
  async setQty(skuId, qty) {
    if (MODE === 'shopify') {
      const id = S.product.shopifyVariantId[skuId];
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: qty }),
      });
      await cartApi.sync();
      return;
    }
    const line = cart.find((l) => l.sku === skuId);
    if (!line) return;
    if (qty <= 0) cart = cart.filter((l) => l.sku !== skuId);
    else line.qty = qty;
    cartApi.save();
  },
  async sync() {
    if (MODE !== 'shopify') return;
    const r = await fetch('/cart.js').then((x) => x.json());
    cart = r.items.map((i) => ({
      sku: i.handle || String(i.variant_id),
      title: i.product_title,
      size: (i.options_with_values?.[0]?.value) || '',
      color: (i.options_with_values?.[1]?.value) || '',
      price: i.price / 100,
      qty: i.quantity,
      img: i.image || (window.NUMA_ASSETS ? window.NUMA_ASSETS['folded.jpg'] : '/images/folded.jpg'),
    }));
    renderCart();
  },
  save() {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    renderCart();
  },
  checkout() {
    if (MODE === 'shopify') { location.href = '/checkout'; return; }
    openModal();
  },
};

const cartTotal = () => cart.reduce((sum, l) => sum + l.price * l.qty, 0);
const cartCount = () => cart.reduce((sum, l) => sum + l.qty, 0);

function renderCart() {
  const n = cartCount();
  const badge = $('#cartCount');
  badge.textContent = n;
  badge.classList.toggle('on', n > 0);

  const box = $('#drItems');
  if (!cart.length) {
    box.innerHTML = '<p class="dr-empty">Nothing in here yet.<br>The crunch is one click away.</p>';
  } else {
    box.innerHTML = cart
      .map(
        (l) => `
      <div class="li">
        <div class="li-img ph"><img src="${l.img}" alt=""><span class="ph-hint"></span></div>
        <div class="li-body">
          <b>${l.title}</b>
          <span>${l.size} · ${l.color}</span>
          <div class="li-qty">
            <button data-dec="${l.sku}" aria-label="Decrease">−</button>
            <em>${l.qty}</em>
            <button data-inc="${l.sku}" aria-label="Increase">+</button>
          </div>
        </div>
        <div>
          <div class="li-price">${money(l.price * l.qty)}</div>
          <a href="#" class="li-rm" data-rm="${l.sku}">Remove</a>
        </div>
      </div>`
      )
      .join('');
  }

  $('#drTotal').textContent = money(cartTotal());
  $('#drCheckout').disabled = !cart.length;

  const left = S.freeShippingThreshold - cartTotal();
  const pct = Math.min(100, (cartTotal() / S.freeShippingThreshold) * 100);
  $('#drShip').innerHTML =
    (left > 0 ? `You're ${money(left)} away from free shipping.` : '✓ Free shipping unlocked.') +
    `<div class="dr-bar"><i style="width:${pct}%"></i></div>`;

  checkImages();
}

$('#drItems').addEventListener('click', (e) => {
  const el = e.target.closest('[data-inc],[data-dec],[data-rm]');
  if (!el) return;
  const { inc, dec, rm } = el.dataset;
  const id = inc || dec || rm;
  const line = cart.find((l) => l.sku === id);
  if (!line) return;
  if (rm) { e.preventDefault(); track('remove_from_cart', { sku: id }); cartApi.setQty(id, 0); }
  else cartApi.setQty(id, line.qty + (inc ? 1 : -1));
});

async function addToCart(origin) {
  const s = sizeOf(state.size);
  const c = colorOf(state.color);
  await cartApi.add({
    sku: sku(),
    title: S.product.title,
    size: `${s.label} ${s.dims}`,
    color: c.label,
    price: s.price,
    qty: state.qty,
    img: (window.NUMA_ASSETS ? window.NUMA_ASSETS['folded.jpg'] : '/images/folded.jpg'),
  });
  track('add_to_cart', {
    sku: sku(), size: state.size, color: state.color,
    qty: state.qty, price: s.price, value: s.price * state.qty, origin,
  });
  $('#cartCount').classList.add('pop');
  setTimeout(() => $('#cartCount').classList.remove('pop'), 420);
  doCrunch(0.55);
  openDrawer('add_to_cart');
}

$('#addToCart').addEventListener('click', () => addToCart('pdp'));
$('#sbAdd').addEventListener('click', () => addToCart('sticky_bar'));

/* ───────────────────────────────────────────── drawer ───────── */

const drawer = $('#drawer');
const scrim = $('#scrim');

function openDrawer(origin) {
  drawer.classList.add('on');
  scrim.classList.add('on');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  track('cart_open', { origin, items: cartCount(), value: cartTotal() });
}
function closeDrawer() {
  drawer.classList.remove('on');
  scrim.classList.remove('on');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
}
$('#cartBtn').addEventListener('click', () => openDrawer('header'));
$('#drClose').addEventListener('click', closeDrawer);
scrim.addEventListener('click', closeDrawer);
addEventListener('keydown', (e) => { if (e.key === 'Escape') { closeDrawer(); closeModal(); } });

/* ───────────────────────────────────────────── checkout ───────── */

const modal = $('#modal');

function openModal() {
  $('#chkSum').innerHTML =
    cart.map((l) => `<div><span>${l.qty} × ${l.title} (${l.size})</span><span>${money(l.price * l.qty)}</span></div>`).join('') +
    `<div class="tot"><span>Total</span><span>${money(cartTotal())}</span></div>`;
  modal.classList.add('on');
  modal.setAttribute('aria-hidden', 'false');
  $('#leadForm').hidden = false;
  $('#done').hidden = true;
  document.body.style.overflow = 'hidden';
  track('checkout_start', { items: cartCount(), value: cartTotal() });
  setTimeout(() => $('#leadForm input[name=name]').focus(), 260);
}
function closeModal() {
  modal.classList.remove('on');
  modal.setAttribute('aria-hidden', 'true');
  if (!drawer.classList.contains('on')) document.body.style.overflow = '';
}
$('#drCheckout').addEventListener('click', () => cartApi.checkout());
$('#modalX').addEventListener('click', closeModal);
$('#doneClose').addEventListener('click', closeModal);
modal.addEventListener('click', (e) => { if (e.target === modal) closeModal(); });

$('#leadForm').addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = $('#leadSubmit');
  const fd = new FormData(e.target);
  btn.disabled = true;
  btn.textContent = 'Reserving…';
  try {
    const res = await fetch(LEAD_URL(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session: sid, source, device,
        name: fd.get('name'), email: fd.get('email'),
        phone: fd.get('phone'), country: fd.get('country'),
        cart, total: cartTotal(),
      }),
    }).then((r) => r.json());
    if (!res.ok) throw new Error(res.error || 'failed');
    $('#orderId').textContent = res.orderId;
    $('#leadForm').hidden = true;
    $('#done').hidden = false;
    cart = [];
    cartApi.save();
    closeDrawer();
    document.body.style.overflow = 'hidden';
    flush(false);
  } catch (err) {
    toast('Something broke on our end — try again in a second.');
    console.error(err);
  } finally {
    btn.disabled = false;
    btn.textContent = 'Reserve my blanket';
  }
});

/* ───────────────────────────────────────────── gallery ───────── */

/* Two different galleries share one strip of markup. On a wide screen the strip
   is a row of thumbnails that swap the big image above it. On a phone there is
   no big image — the strip itself is the swipeable gallery — so a tap must bring
   the neighbouring shot into view instead of changing something invisible. */
const thumbStrip = $('#galThumbs');
const stripIsCarousel = () => getComputedStyle(thumbStrip).display === 'flex';

thumbStrip.addEventListener('click', (e) => {
  const t = e.target.closest('.thumb');
  if (!t) return;

  if (stripIsCarousel()) {
    t.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  } else {
    const main = $('#galMain');
    main.style.opacity = '0';
    setTimeout(() => {
      main.src = t.dataset.src;
      main.style.opacity = '1';
      checkImages();
    }, 160);
  }

  $$('.thumb').forEach((x) => x.classList.remove('is-active'));
  t.classList.add('is-active');
  track('gallery_view', { src: t.dataset.src });
});

/* Dots, so a phone user can see there are nine shots and not just the one. */
const dots = document.createElement('div');
dots.className = 'gal-dots';
const shots = $$('.thumb', thumbStrip);
shots.forEach(() => dots.appendChild(document.createElement('i')));
thumbStrip.after(dots);

function syncDots() {
  const mid = thumbStrip.scrollLeft + thumbStrip.clientWidth / 2;
  let nearest = 0;
  let best = Infinity;
  shots.forEach((t, i) => {
    const centre = t.offsetLeft + t.offsetWidth / 2;
    const d = Math.abs(centre - mid);
    if (d < best) { best = d; nearest = i; }
  });
  [...dots.children].forEach((d, i) => d.classList.toggle('on', i === nearest));
}
thumbStrip.addEventListener('scroll', () => requestAnimationFrame(syncDots), { passive: true });
syncDots();

/* Photos not dropped in yet? Show the expected filename instead of a broken icon. */
function checkImages() {
  $$('.ph img').forEach((img) => {
    const box = img.closest('.ph');
    const bad = img.complete && img.naturalWidth === 0;
    box.classList.toggle('is-missing', bad);
    if (!img.dataset.bound) {
      img.dataset.bound = '1';
      img.addEventListener('error', () => box.classList.add('is-missing'));
      img.addEventListener('load', () => box.classList.remove('is-missing'));
    }
  });
}

/* ───────────────────────────────────────────── scroll behaviour ───────── */

const io = new IntersectionObserver(
  (entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('in'); io.unobserve(en.target); } }),
  { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
);
$$('.reveal').forEach((el) => io.observe(el));

const seenSections = new Set();
const sectionIo = new IntersectionObserver(
  (entries) =>
    entries.forEach((en) => {
      const id = en.target.dataset.section;
      if (en.isIntersecting && !seenSections.has(id)) {
        seenSections.add(id);
        track('section_view', { id });
      }
    }),
  { threshold: 0.35 }
);
$$('[data-section]').forEach((el) => sectionIo.observe(el));

const depths = [25, 50, 75, 100];
const hitDepths = new Set();
let ticking = false;
addEventListener('scroll', () => {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    const y = scrollY;
    const max = document.body.scrollHeight - innerHeight;
    const pct = max > 0 ? (y / max) * 100 : 0;
    depths.forEach((d) => {
      if (pct >= d && !hitDepths.has(d)) { hitDepths.add(d); track('scroll_depth', { depth: d }); }
    });
    $('#hdr').classList.toggle('stuck', y > 12);
    const buyRect = $('#buy').getBoundingClientRect();
    const past = y > innerHeight * 0.8;
    const inBuy = buyRect.top < innerHeight * 0.9 && buyRect.bottom > innerHeight * 0.4;
    $('#stickybar').classList.toggle('on', past && !inBuy);
    ticking = false;
  });
}, { passive: true });

$$('[data-track]').forEach((el) => el.addEventListener('click', () => track('cta_click', { id: el.dataset.track })));
$$('#acc details').forEach((d) =>
  d.addEventListener('toggle', () => { if (d.open) track('faq_open', { q: d.querySelector('summary').textContent.trim().slice(0, 60) }); })
);

/* ───────────────────────────────────────────── toast ───────── */

let toastTimer = null;
function toast(msg) {
  const t = $('#toast');
  t.textContent = msg;
  t.classList.add('on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('on'), 3200);
}

/* ───────────────────────────────────────────── boot ───────── */

loadTikTokPixel(S.tiktokPixelId);
renderStoreText();
renderOptions();
renderPrice();
renderCart();
checkImages();
addEventListener('load', checkImages);
if (MODE === 'shopify') cartApi.sync();
track('page_view', { referrer: document.referrer.slice(0, 120), w: innerWidth });

})();
